/*
 * <store-services-accordion> — VIS-130.
 *
 * Lists the services offered by the visitor's favorite store inside the
 * accordion-row it is placed in. The store is only known client-side (the
 * favorite lives in localStorage as `vo:favoriteStore`, written by
 * store-favorites.js), so the list is rendered empty on the server and filled
 * here:
 *
 *  - With no favorite set it shows the `[data-services-empty]` placeholder so
 *    the host accordion row is never blank (the `data-store-favorite-name`
 *    state).
 *  - When a favorite exists we fetch that store's page section via the Section
 *    Rendering API (reusing store-detail's services markup) and lift the
 *    service names out of `.store-detail__service-name` into the list.
 *  - It re-renders on `store-favorite:changed` so switching/clearing the
 *    favorite updates the list in place; results are memoised per store handle
 *    so re-emitted events don't refetch.
 */

import { getFavorite, favoriteStoreUrl } from '@theme/store-favorites';

class StoreServicesAccordion extends HTMLElement {
  connectedCallback() {
    this.list = this.querySelector('[data-services-list]');
    this.empty = this.querySelector('[data-services-empty]');
    this.sectionId = this.dataset.storeSectionId || 'store-detail';
    this.loadedHandle = null;
    this.loading = false;

    window.addEventListener('store-favorite:changed', this.#onFavoriteChange);
    void this.#render();
  }

  disconnectedCallback() {
    window.removeEventListener('store-favorite:changed', this.#onFavoriteChange);
    this.controller?.abort();
  }

  #onFavoriteChange = () => {
    void this.#render();
  };

  async #render() {
    const favorite = getFavorite();

    if (!favorite) {
      this.#showEmpty();
      this.loadedHandle = null;
      return;
    }

    // Already showing this store — nothing to do.
    if (favorite.handle === this.loadedHandle) return;

    await this.#load(favorite);
  }

  /** @param {{ handle: string, name: string, url: string }} favorite */
  async #load(favorite) {
    if (this.loading) return;
    this.loading = true;
    this.controller?.abort();
    this.controller = new AbortController();

    try {
      // favoriteStoreUrl() derives the page from the handle when the stored url
      // is empty; an unpublished store page 404s into the empty state below.
      const url = `${favoriteStoreUrl(favorite)}?section_id=${encodeURIComponent(this.sectionId)}`;
      const response = await fetch(url, { credentials: 'same-origin', signal: this.controller.signal });
      if (!response.ok) throw new Error(`store section responded ${response.status}`);

      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const names = Array.from(doc.querySelectorAll('.store-detail__service-name'))
        .map((el) => el.textContent?.trim() || '')
        .filter(Boolean);

      if (names.length === 0) {
        this.#showEmpty();
      } else {
        this.#populate(names);
      }
      this.loadedHandle = favorite.handle;
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
      console.warn('[store-services-accordion] could not load the favorite store services.', error);
      this.#showEmpty();
      // Remember the attempt too: a store whose page 404s (e.g. unpublished)
      // would otherwise refetch and warn on every store-favorite:changed.
      this.loadedHandle = favorite.handle;
    } finally {
      this.loading = false;
    }
  }

  #showEmpty() {
    if (this.list) {
      this.list.replaceChildren();
      this.list.hidden = true;
    }
    if (this.empty) this.empty.hidden = false;
  }

  /** @param {string[]} names */
  #populate(names) {
    if (this.list) {
      this.list.replaceChildren(
        ...names.map((name) => {
          const item = document.createElement('li');
          item.className = 'store-services-accordion__item';
          item.textContent = name;
          return item;
        })
      );
      this.list.hidden = false;
    }
    if (this.empty) this.empty.hidden = true;
  }
}

if (!customElements.get('store-services-accordion')) {
  customElements.define('store-services-accordion', StoreServicesAccordion);
}
